// The keyboard listener and the cheat sheet. The decisions all live in
// state/shortcuts.ts, which is tested without a browser; this is the wiring.
import { useEffect, useRef, useState } from "react";
import { resolve, CHEAT_SHEET, type Shortcut } from "../state/shortcuts";

/* EVERY HANDLER IS OPTIONAL, and an action with no handler is not taken at all --
   no preventDefault, no swallowed key. That is what lets two listeners divide the
   keyboard honestly: the shell owns Ctrl-K because the palette is global, the
   tracker owns Space and the number keys because the turn order is not. Whichever
   page is mounted answers for its own keys and no others. */
export interface Handlers {
  next?: () => void;
  previous?: () => void;
  search?: () => void;
  pick?: (index: number) => void;
  close?: () => void;
  help?: () => void;
}

/** One window listener for the whole combat page. */
export function useShortcuts(enabled: boolean, handlers: Handlers) {
  /* The handlers are read through a ref so the listener is attached ONCE.
     Passing them as an effect dependency would detach and reattach on every
     render of the busiest page in the console -- several times a second during a
     turn -- and a listener that is being replaced is a listener that can miss a
     key. */
  const live = useRef(handlers);
  live.current = handlers;
  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;
    const onKey = (ev: KeyboardEvent) => {
      const el = (ev.target as HTMLElement | null) || null;

      /* A MODAL MEANS THE REST OF THE PAGE IS UNAVAILABLE, and its keyboard
         shortcuts are part of the page.

         Found by reading my own code rather than by a test: with the search
         palette open and focus on a result row (a button, so not "typing"), a
         number key still resolved to "pick" and opened a tracker row's hit
         points BEHIND the modal. The DM would have been typing damage into a
         row they could not see.

         The same rule S8-6 applies to Tab: whatever is inside the modal keeps
         working, everything outside it waits. */
      const modal = typeof document === "undefined" ? null : document.querySelector('[aria-modal="true"]');
      if (modal && !(el && modal.contains(el))) return;

      const tag = el?.tagName;
      /* `closest` is what makes "a button has focus" true for an icon inside a
         button, which is most of this console's buttons. */
      const pressable = Boolean(el?.closest?.('button, a, [role="button"], summary'));
      const hit: Shortcut | null = resolve({
        key: ev.key, ctrlKey: ev.ctrlKey, metaKey: ev.metaKey, shiftKey: ev.shiftKey, altKey: ev.altKey,
        tag, editable: Boolean(el?.isContentEditable), pressable, defaultPrevented: ev.defaultPrevented,
      });
      if (!hit) return;
      /* Nobody here handles it: leave the key entirely alone. */
      const handler = live.current[hit.action];
      if (!handler) return;
      /* Taken only once it is certainly ours: a Space that scrolls the page is
         the single most annoying way for this to go wrong. */
      ev.preventDefault();
      if (hit.action === "pick") live.current.pick?.(hit.index || 1);
      else (handler as () => void)();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [enabled]);
}

/** Reach the nth row the DM can see.
 *
 *  FOCUS, NOT ACTION. The number key lands on the row's hit points ready to
 *  type into -- it does not change a turn, roll anything or write anywhere,
 *  because a mistyped "5" must cost nothing. Escape backs out of the field the
 *  way Escape already does everywhere else. */
export function focusRow(index: number): boolean {
  if (typeof document === "undefined") return false;
  const row = document.querySelector(`[data-row="${index}"]`);
  if (!row) return false;
  row.scrollIntoView({ block: "nearest" });
  /* The hit-point field when the row has one (party members do); otherwise the
     row's own controls, so the keyboard still arrives somewhere useful. */
  const hp = row.querySelector(".hp .numfield") as HTMLElement | null;
  if (hp) { hp.click(); return true; }
  const first = row.querySelector("button") as HTMLElement | null;
  if (first) { first.focus(); return true; }
  return false;
}

/** The list of keys, shown by `?` and by the button beside it. */
export function ShortcutHelp({ onClose }: { onClose: () => void }) {
  return (
    <div className="shortcut-help" role="region" aria-label="Keyboard shortcuts">
      <div className="row">
        <strong>Keyboard</strong>
        <button className="ghost small" onClick={onClose} aria-label="Close the keyboard shortcut list">Close</button>
      </div>
      <dl>
        {CHEAT_SHEET.map((r) => <div key={r.keys}><dt><kbd>{r.keys}</kbd></dt><dd>{r.does}</dd></div>)}
      </dl>
      <p className="muted small">
        No shortcut fires while you are typing &mdash; not even Escape, which the field it is in handles.
        A number key opens that row&rsquo;s hit points ready to type into: &ldquo;3&rdquo; then &ldquo;-10&rdquo; is ten damage to the third row.
      </p>
    </div>
  );
}

/** The off switch, and the way the keys are discovered at all. */
export function ShortcutToggle({ on, setOn, onHelp }: { on: boolean; setOn: (v: boolean) => void; onHelp: () => void }) {
  return (
    <span className="shortcut-toggle">
      <button className={`ghost small ${on ? "on" : ""}`} aria-pressed={on}
        title={on ? "Keyboard shortcuts are on: Space for the next turn, 1-9 for a row, Ctrl-K to search. Click to turn them off." : "Turn on keyboard shortcuts: Space for the next turn, 1-9 for a row, Ctrl-K to search."}
        onClick={() => setOn(!on)}>{on ? "Keys on" : "Keys off"}</button>
      {on && <button className="link small" onClick={onHelp} aria-label="Show the keyboard shortcuts" title="What the keys do (or press ?)">?</button>}
    </span>
  );
}

/** Kept out of the hook so a test can use the same state shape. */
export function useHelpVisible(): [boolean, (v: boolean) => void] {
  const [open, setOpen] = useState(false);
  return [open, setOpen];
}
